import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { Amount } from "models/utilTypes/currencyType";
import { ReportHanlder, ReportProps } from "models/propTypes/accountingPropTypes";
import { NestedTableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { DrillDownTable, useDownload } from "components/common";
import useFinancialReportRow from "components/accounting/hooks/useFinancialReportRow";
import useApplyExpandAllRows from "../hooks/useApplyExpandAllRows";
import useMonthlyReportHelpers from "components/accounting/hooks/useMonthlyReportHelpers";
import useMonthlyTransactionAmount from "components/accounting/hooks/useMonthlyTransactionAmount";
import PrintMonthlyResultReport from "components/accounting/resultReport/print/monthly";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { DateHelper, ExportHelper, FinancialReportHelper } from "utils/helpers";

const MonthlyIncomeStatement: React.ForwardRefRenderFunction<ReportHanlder, ReportProps> = (
  { updateReportExportData, reloadKey, reload },
  ref
) => {
  const financialReportService = useFinancialReportService();
  const { result, filter, loading, pageFilter } = useMonthlyReportHelpers(
    financialReportService.getMonthlyResultReport
  );

  const { handleDownloadPDFRender } = useDownload();

  const tableRef = React.useRef<NestedTableHandles>(null);

  React.useImperativeHandle(ref, () => ({
    print: (title?: string, subtitle?: string, fileName?: string) => {
      handleDownloadPDFRender(
        <PrintMonthlyResultReport
          title={title}
          subtitle={subtitle}
          result={result}
          amountFormatter={convertSign}
          amountHeaderDescription={headerDescription}
          showAccountNumber={pageFilter?.showAccountNumber ?? false}
        />,
        `${fileName}.pdf`
      );
    },
    expandAll: () => tableRef.current?.expandAll(),
    collapseAll: () => tableRef.current?.collapseAll(),
  }));

  const headerDescription = (repeatIndex: number) =>
    filter?.startMonth ? DateHelper.formatAPIYearMonthToNumericString(filter.startMonth, repeatIndex) : "";

  const convertSign = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    row.amount ? row.amount * -1 : row.amount;

  const formatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, convertSign(row));

  const { transactionAmountFormatter } = useMonthlyTransactionAmount(filter, convertSign);

  const { descriptionFormatter } = useFinancialReportRow(pageFilter?.showAccountNumber ?? false);

  React.useEffect(() => {
    const headers = ExportHelper.generateSheetHeaders(result, (_, index) => [headerDescription(index)]);
    const sheet = ExportHelper.generateSheetData(result, descriptionFormatter, (col) => [formatter(col).amount]);
    updateReportExportData(headers.concat(sheet));
  }, [result, pageFilter, descriptionFormatter]);

  React.useEffect(() => {
    reload();
  }, [pageFilter?.showAccountNumber]);

  useApplyExpandAllRows(tableRef, result, pageFilter?.expandAllRows);

  return (
    <DrillDownTable<
      WintSuperkollAPIModelFinancialReport.FinancialReportRow,
      WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn
    >
      key={reloadKey}
      tableRef={tableRef}
      rows={result}
      rowTextProperty={{
        key: "description",
        properties: {
          description: TranslationKeys.Common.Name,
          formatter: descriptionFormatter,
        },
      }}
      repeatProperties={{
        key: "columns",
        properties: {
          amount: {
            description: headerDescription,
            formatter: transactionAmountFormatter,
            alignment: "right",
          },
        },

        sumNegative: (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) => row.amount > 0,
      }}
      childrenKey="children"
      rowDisplayType={FinancialReportHelper.getResultReportRowDisplayType}
      showTotal={{
        key: "amount",
        valueFormatter: convertSign,
        displayFormatter: (value: number) => new Amount(undefined, value),
      }}
      loading={loading}
    />
  );
};

export default React.forwardRef(MonthlyIncomeStatement);
